// src/components/communications/Communications.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Tabs, Tab, Paper, Grid, Card, CardContent, Typography, 
  Chip, List, ListItem, ListItemText, ListItemIcon, Skeleton, Alert
} from '@mui/material';
import { 
  Email as EmailIcon, 
  Sms as SmsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import CommunicationLogs from './EmailLogs';
import { communicationAPI } from '../../services/api';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';

const Communications = () => {
  const [tabValue, setTabValue] = useState(0);
  const [innerTab, setInnerTab] = useState(0);
  const { can } = useAccessControl();
  const canReadEmail = can(RESOURCES.COMMUNICATIONS.EMAIL, ACTIONS.READ);
  const canReadSms = can(RESOURCES.COMMUNICATIONS.SMS, ACTIONS.READ);
  const canRead = canReadEmail || canReadSms;
  const [stats, setStats] = useState({
    emailsSent: 0,
    emailsFailed: 0,
    smsSent: 0,
    smsScheduled: 0
  });
  const [recentEmails, setRecentEmails] = useState([]);
  const [recentSms, setRecentSms] = useState([]);
  const [smsStatistics, setSmsStatistics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tabValue === 0 && canRead) {
      fetchDashboardData();
    }
  }, [tabValue, canRead]);

  useEffect(() => {
    if (tabValue === 0 && innerTab === 0 && canRead) {
      fetchDashboardData();
    }
  }, [innerTab, canRead]);

  const fetchDashboardData = async () => {
    try {
      const [emailResponse, smsResponse, smsStatsResponse] = await Promise.all([
        communicationAPI.getEmailLogs({ per_page: 5 }),
        communicationAPI.getSmsLogs({ per_page: 5 }),
        communicationAPI.getSmsStatistics({ year: new Date().getFullYear() })
      ]);
      
      const emails = emailResponse.data.data || emailResponse.data || [];
      const sms = smsResponse.data.data || smsResponse.data || [];
      const smsStats = smsStatsResponse.data.data || [];
      
      setRecentEmails(emails);
      setRecentSms(sms);
      setSmsStatistics(smsStats);
      
      setStats({
        emailsSent: emails.filter(e => e.status === 'sent').length,
        emailsFailed: emails.filter(e => e.status === 'failed').length,
        smsSent: sms.filter(s => s.status === 'sent').reduce((sum, s) => sum + (s.delivered_count || 0), 0),
        smsScheduled: sms.filter(s => s.status === 'scheduled').length
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" color={color}>{value}</Typography>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
          </Box>
          <Box color={`${color}.main`}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (!canRead) return <Alert severity="error">You do not have permission to view communications.</Alert>;

  return (
    <Box>
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Dashboard" />
          <Tab label="Communication Logs" />
        </Tabs>
      </Paper>

      <Box>
        {tabValue === 0 && (
          <Box>
            <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight={700}>Communications Dashboard</Typography>
                <Typography variant="body1" color="text.secondary">
                  Overview of email and SMS communications
                </Typography>
              </Box>
            </Box>
            
            {loading ? (
              <Grid container spacing={3}>
                {[1,2,3,4].map(i => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Skeleton variant="rectangular" height={100} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                      title="Emails Sent" 
                      value={stats.emailsSent} 
                      icon={<CheckCircleIcon />} 
                      color="success" 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                      title="Email Failures" 
                      value={stats.emailsFailed} 
                      icon={<ErrorIcon />} 
                      color="error" 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                      title="SMS Delivered" 
                      value={stats.smsSent} 
                      icon={<SmsIcon />} 
                      color="info" 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                      title="SMS Scheduled" 
                      value={stats.smsScheduled} 
                      icon={<ScheduleIcon />} 
                      color="warning" 
                    />
                  </Grid>
                </Grid>
                
                {/* SMS Statistics Summary */}
                {smsStatistics.length > 0 && (
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>SMS Statistics ({new Date().getFullYear()})</Typography>
                      <Grid container spacing={2}>
                        {smsStatistics.slice(0, 6).map((stat, index) => {
                          const [year, month] = stat.month_year.split('-');
                          const monthName = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short' });
                          return (
                            <Grid item xs={6} sm={4} md={2} key={index}>
                              <Box textAlign="center" p={1} bgcolor="grey.50" borderRadius={1}>
                                <Typography variant="h6" color="primary">{stat.total_sent}</Typography>
                                <Typography variant="caption" color="text.secondary">{monthName}</Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </CardContent>
                  </Card>
                )}
                
                <Card elevation={3}>
                  <CardContent>
                    <Tabs value={innerTab} onChange={(e, v) => setInnerTab(v)} sx={{ mb: 2 }}>
                      <Tab label="Recent Emails" />
                      <Tab label="Recent SMS" />
                    </Tabs>

                    {innerTab === 0 && (
                      <List>
                        {recentEmails.slice(0, 5).map(email => (
                          <ListItem key={email.id}>
                            <ListItemIcon>
                              <EmailIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={email.subject}
                              secondary={`To: ${email.recipient_name} - ${new Date(email.created_at).toLocaleDateString()}`}
                            />
                            <Chip 
                              label={email.status} 
                              color={email.status === 'sent' ? 'success' : 'error'} 
                              size="small" 
                            />
                          </ListItem>
                        ))}
                        {recentEmails.length === 0 && (
                          <ListItem>
                            <ListItemText primary="No recent emails" />
                          </ListItem>
                        )}
                      </List>
                    )}

                    {innerTab === 1 && (
                      <List>
                        {recentSms.slice(0, 5).map(sms => (
                          <ListItem key={sms.id}>
                            <ListItemIcon>
                              <SmsIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={`${sms.recipient_count} recipients`}
                              secondary={`${sms.message?.substring(0, 40)}... - ${new Date(sms.created_at).toLocaleDateString()}`}
                            />
                            <Chip 
                              label={sms.status} 
                              color={
                                sms.status === 'sent' ? 'success' : 
                                sms.status === 'scheduled' ? 'warning' : 'error'
                              } 
                              size="small" 
                            />
                          </ListItem>
                        ))}
                        {recentSms.length === 0 && (
                          <ListItem>
                            <ListItemText primary="No recent SMS" />
                          </ListItem>
                        )}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        )}
        
        {tabValue === 1 && <CommunicationLogs />}
      </Box>
    </Box>
  );
};

export default Communications;